import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { apiPost } from '@/utils/api';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'no-token'
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await apiPost(`/auth/verify-email?token=${token}`);
        const data = await response.json();

        if (response.ok) {
          setUserData(data);
          setStatus('success');
          
          // After showing success, redirect to 2FA verification
          setTimeout(() => {
            navigate('/app/verify-2fa', {
              state: {
                user_id: data.user_id,
                email: data.email,
                fromEmailVerification: true
              },
              replace: true
            });
          }, 3000);
        } else {
          setError(data.detail || 'Verification failed');
          setStatus('error');
        }
      } catch (err) {
        setError('An error occurred. Please try again.');
        setStatus('error');
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md text-center">
        {/* Verifying State */}
        {status === 'verifying' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-full border-4 border-emerald-200"></div>
              <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl">✉️</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h1>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <span className="text-4xl">✅</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
            <p className="text-gray-600 mb-4">
              Welcome to GuapGoals, <strong>{userData?.name}</strong>!
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-blue-600">🔐</span>
                <span className="font-semibold text-blue-800">2FA Enabled for Your Security</span>
              </div>
              <p className="text-sm text-blue-700">
                We&apos;ve sent a verification code to your email. You&apos;ll need to enter it to complete setup.
              </p>
            </div>
            <p className="text-sm text-gray-500">Redirecting to verification in 3 seconds...</p>
            <div className="mt-4">
              <Link
                to="/app/verify-2fa"
                state={{ user_id: userData?.user_id, email: userData?.email, fromEmailVerification: true }}
                className="text-emerald-600 hover:text-emerald-800 font-medium"
              >
                Click here if not redirected →
              </Link>
            </div>
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            <p className="text-red-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Link
                to="/app/login"
                className="block w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all"
              >
                Back to Login
              </Link>
              <p className="text-sm text-gray-500">
                Need a new verification link?{' '}
                <Link to="/app/resend-verification" className="text-emerald-600 hover:text-emerald-800">
                  Resend verification email
                </Link>
              </p>
            </div>
          </>
        )}

        {/* No Token State */}
        {status === 'no-token' && (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Verification Link</h1>
            <p className="text-gray-600 mb-6">
              This verification link is invalid or missing. Please check your email for the correct link.
            </p>
            <div className="space-y-3">
              <Link
                to="/app/login"
                className="block w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all"
              >
                Back to Login
              </Link>
            </div>
          </>
        )}

        {/* Security Info */}
        <div className="mt-8 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-emerald-600">🛡️</span>
            <span className="font-semibold text-emerald-800 text-sm">Why Email Verification?</span>
          </div>
          <ul className="text-xs text-emerald-700 space-y-1 text-left">
            <li>✓ Protects your account from unauthorized access</li>
            <li>✓ Ensures you receive important notifications</li>
            <li>✓ Enables password recovery if needed</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
